import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, useDisclosure } from "@chakra-ui/react";
import { ColumnDef, PaginationState, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import CDS from "design-system";
import { useSearchParams } from "react-router";
import BaseRowTable from "components/Table/BaseRowTable";
import { Task } from "features/Resources/Task/models";
import { useTaskListResponseQuery } from "features/Resources/Task/hooks";
import { createV2TaskColumns } from "features/Resources/Task/Components/Table/V2TasksTable/columns";
import { createPaginationQueryString } from "helpers/queryString";
import SendReminderModal from "features/SendMessage/Components/SendReminderModal";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";

interface SortOption {
  label: string;
  value: string;
}

interface V2TasksTableServerSideProps {
  queryString?: string;
  onExpand: (item: Task) => void;
  sortOptions?: SortOption[];
  currentSort?: string;
  defaultSortString?: string;
  onSortChange?: (value: string) => void;
  showTaskActionButtons?: boolean;
  showJourney?: boolean;
}

const PAGE_SIZE_QUERY_PARAM = "ps";
const PAGE_INDEX_QUERY_PARAM = "pi";
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 40, 60, 80];

export default function V2TasksTableServerSide({
  queryString = "",
  onExpand,
  sortOptions,
  currentSort,
  defaultSortString,
  onSortChange,
  showTaskActionButtons = false,
  showJourney = false,
}: V2TasksTableServerSideProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Seed pagination from the URL so deep links to `?ps=20&pi=3` land on the
  // right page. Subsequent state changes are mirrored back to the URL below.
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: Number(searchParams.get(PAGE_INDEX_QUERY_PARAM)) || 0,
    pageSize: Number(searchParams.get(PAGE_SIZE_QUERY_PARAM)) || DEFAULT_PAGE_SIZE,
  }));

  // When the parent's filters/sort/search change, jump back to page 0 — page 5
  // of "all" is meaningless once you've narrowed to "overdue".
  const initialQueryStringRef = useRef(queryString);
  useEffect(() => {
    if (initialQueryStringRef.current === queryString) return;
    initialQueryStringRef.current = queryString;
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [queryString]);

  // Mirror pagination → URL. Functional setter keeps unrelated URL changes from re-firing this.
  useEffect(() => {
    setSearchParams(
      (params) => {
        params.set(PAGE_SIZE_QUERY_PARAM, String(pagination.pageSize));
        params.set(PAGE_INDEX_QUERY_PARAM, String(pagination.pageIndex));
        return params;
      },
      { replace: true },
    );
  }, [pagination.pageIndex, pagination.pageSize, setSearchParams]);

  const paginatedQueryString = useMemo(() => {
    const pageParam = createPaginationQueryString(pagination.pageIndex + 1, pagination.pageSize);
    return queryString ? `${queryString}&${pageParam}` : pageParam;
  }, [queryString, pagination.pageIndex, pagination.pageSize]);

  const { data: response, isLoading } = useTaskListResponseQuery(paginatedQueryString);
  const tasks = response?.data ?? [];
  const pageCount = response?.meta?.pagination?.totalPages ?? -1;

  const reminderDisclosure = useDisclosure();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const handleOpenRemind = useCallback((task: Task) => {
    setSelectedTask(task);
    reminderDisclosure.onOpen();
  }, [reminderDisclosure]);

  const columns = useMemo<ColumnDef<Task, any>[]>(
    () => createV2TaskColumns(onExpand, handleOpenRemind, showTaskActionButtons, showJourney),
    [handleOpenRemind, onExpand, showJourney, showTaskActionButtons],
  );

  const table = useReactTable({
    data: tasks,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <>
      <BaseRowTable
        table={table}
        isLoading={isLoading}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        sx={{ backgroundColor: "transparent", width: "100%" }}
        containerSx={{ width: "100%" }}
        sortOptions={sortOptions}
        currentSort={currentSort}
        defaultSortString={defaultSortString}
        onSortChange={onSortChange}
        emptyStateComponent={(
          <Box textAlign="center" p={4}>
            <CDS.Text16 color="chakra-subtle-text">
              No tasks found
            </CDS.Text16>
          </Box>
        )}
      />
      {selectedTask && (
        <SendReminderModal
          isOpen={reminderDisclosure.isOpen}
          onClose={reminderDisclosure.onClose}
          tasks={[selectedTask]}
          preselectedUser={selectedTask.assignee}
        />
      )}
      <TaskActionModalByQueryParam />
    </>
  );
}
